import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ShowCard from '@/components/feature/ShowCard';
import { getShowById, getRelatedShows } from '@/mocks/shows';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const show = getShowById(id || '');

  if (!show) {
    return (
      <div className="min-h-screen bg-background-50 pt-16">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-2">剧集未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要找的剧集不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg font-medium text-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedShows = getRelatedShows(show);
  const categoryLabelMap: Record<string, string> = {
    korean: '韩剧',
    chinese: '国产剧',
    american: '美剧',
    japanese: '日剧',
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-3">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: show.category, to: `/category/${show.categorySlug}` },
                { label: show.title },
              ]}
            />
          </div>
        </div>

        {/* Show Detail */}
        <article className="py-8 md:py-12">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Poster */}
              <div className="w-full lg:w-[300px] shrink-0">
                <div className="relative overflow-hidden rounded-xl aspect-[3/4] bg-background-200">
                  <img
                    src={show.image}
                    alt={`${show.title} ${show.category} - 琪琪影院`}
                    title={`${show.title} - ${show.category} 电视剧全集`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-500 text-white text-sm font-bold rounded-lg">
                      <i className="ri-star-fill text-xs"></i>
                      {show.rating}
                    </span>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {show.title}
                </h1>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {show.genre.map((g) => (
                    <span key={g} className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-50 text-secondary-700">
                      {g}
                    </span>
                  ))}
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-50 text-accent-700">
                    {show.category}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    show.status === '已完结' ? 'bg-green-50 text-green-700' : 'bg-primary-50 text-primary-700'
                  }`}>
                    {show.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-4 rounded-xl bg-background-100">
                  {[
                    { label: '年份', value: show.year },
                    { label: '集数', value: `${show.episodes}集` },
                    { label: '导演', value: show.director },
                    { label: '更新状态', value: show.updateInfo },
                  ].map((item) => (
                    <div key={item.label}>
                      <p className="text-xs text-foreground-400 mb-1">{item.label}</p>
                      <p className="text-sm font-medium text-foreground-950">{item.value}</p>
                    </div>
                  ))}
                </div>

                <div className="mb-6">
                  <h2 className="text-base font-heading font-semibold text-foreground-950 mb-2">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{show.description}</p>
                </div>

                <div>
                  <h2 className="text-base font-heading font-semibold text-foreground-950 mb-2">主演阵容</h2>
                  <div className="flex flex-wrap gap-2">
                    {show.cast.map((actor) => (
                      <span
                        key={actor}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-sm text-foreground-700 border border-background-200"
                      >
                        <i className="ri-user-line text-foreground-400 text-xs"></i>
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Related Shows */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  相关推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">你可能也会喜欢这些剧集</p>
              </div>
              <Link
                to={`/category/${show.categorySlug}`}
                className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                更多{show.category}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-product-shop>
              {relatedShows.map((s) => (
                <ShowCard key={s.id} show={s} variant="compact" />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          'headline': `${show.title} - 琪琪影院${show.category}推荐`,
          'description': show.description,
          'author': {
            '@type': 'Organization',
            'name': '琪琪影院',
          },
          'publisher': {
            '@type': 'Organization',
            'name': '琪琪影院',
            'url': 'https://qiyicinema.com',
          },
          'datePublished': `${show.year}-01-01`,
          'dateModified': show.updateInfo,
          'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': `https://qiyicinema.com/article/${show.id}`,
          },
        }),
      }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': '首页',
              'item': 'https://qiyicinema.com/',
            },
            {
              '@type': 'ListItem',
              'position': 2,
              'name': show.category,
              'item': `https://qiyicinema.com/category/${show.categorySlug}`,
            },
            {
              '@type': 'ListItem',
              'position': 3,
              'name': show.title,
            },
          ],
        }),
      }} />
    </div>
  );
}